import {Stub} from '@/api/models';
import {FeedFile, FeedFiles} from '@/api/models/FeedFiles.ts';
import Pending from '@/components/Pending.tsx';
import {Button, MenuItem, SpinnerSize} from '@blueprintjs/core';
import {ItemRenderer, Select} from '@blueprintjs/select';
import {ReactElement, useCallback, useMemo} from 'react';

export type FeedFileStub = Stub<FeedFile, 'name' | 'path'>;

interface Props {
	current: FeedFileStub | null,
	onChange: (item: FeedFile) => void,
}

export default function FeedFileSelect({current, onChange}: Props): ReactElement {
	const files = useMemo(async () => {
		const files = await FeedFiles.list();
		return files.sort((a, b) => {
			if (a.name && !b.name)
				return -1;
			else if (b.name && !a.name)
				return 1;

			const aVal = a.name ?? a.path;
			const bVal = b.name ?? b.path;

			return aVal.localeCompare(bVal);
		});
	}, []);

	return (
		<Pending resource={files} inline={true} size={SpinnerSize.SMALL}>
			{items => <Content onChange={onChange} current={current} files={items} />}
		</Pending>
	);
}

interface ContentProps {
	files: FeedFile[],
	onChange: (item: FeedFile) => void,
	current: FeedFileStub | null,
}

function Content({files, onChange, current}: ContentProps): ReactElement {
	const onItemSelect = useCallback((item: FeedFile) => {
		onChange(item);
	}, [onChange]);

	const itemRenderer: ItemRenderer<FeedFile> = useCallback((item, {handleClick, handleFocus, modifiers}) => {
		if (!modifiers.matchesPredicate)
			return null;

		return (
			<MenuItem
				key={item.id}
				active={modifiers.active}
				selected={item.id === current?.id}
				text={item.name ?? item.path}
				label={item.name ? item.path : undefined}
				roleStructure="listoption"
				onClick={handleClick}
				onFocus={handleFocus}
			/>
		);
	}, [current]);

	return (
		<Select
			items={files}
			itemRenderer={itemRenderer}
			itemPredicate={itemPredicate}
			onItemSelect={onItemSelect}
			fill={true}
			popoverProps={{matchTargetWidth: true}}
			noResults={<MenuItem text="No files match your search." />}
		>
			<Button
				text={current?.name ?? current?.path ?? 'Select a file...'}
				endIcon="chevron-down"
				alignText="start"
				fill={true}
			/>
		</Select>
	);
}

function itemPredicate(query: string, item: FeedFile): boolean {
	return `${item.name} ${item.path}`.toLowerCase().includes(query.toLowerCase());
}
